import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  Avatar,
  FormHelperText,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useListAllUsersQuery,
  useResetPasswordMutation,
} from '../../store/api/userApi';
import { User } from '../../types/user.types';
import { useGetDepartmentsDropdownQuery } from '../../store/api/departmentAPI';
import { formatDate as formatDateUtil } from '@/utils/formatters';
import Images from '@/assets/Images';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { isFieldEnabled } from '@/utils/helpers';
import { useUpdateWorkflowTransactionMutation } from '@/store/api/workflowTransactionApi';
import { useGetRolesDropdownQuery } from '@/store/api/roleApi';
import AutoCompleteSelect from '@/components/common/AutoCompleteSelect';
import PhoneInput from '@/components/common/PhoneInput/PhoneInput';
import { useGetPhoneCountryCodesQuery } from '@/store/api/phoneCodeApi';
import { useForm, Controller } from 'react-hook-form';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';
import { parseApiError } from '@/utils/apiFieldErrors';
import { downloadCredentialsExcel } from '@/utils/downloadCredentialsExcel';
import toast from '@/utils/toast';

const FIELD_GRID = 3; // 4 fields per row

interface UserEditProps {
  initialData?: any;
  isWorkflowEdit?: boolean;
  modifiable?: string[];
  workflowTransaction?: string;
  setIsEditWorkflow?: React.Dispatch<React.SetStateAction<boolean>>;
  approvalTaskRefetch?: () => void;
}

const UserEdit: React.FC<UserEditProps> = ({
  isWorkflowEdit = false,
  initialData,
  modifiable,
  workflowTransaction,
  setIsEditWorkflow,
  approvalTaskRefetch
}) => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { companyId } = useCompanyContext();
  const localStorageCompanyId = localStorage.getItem('companyId') || '';
  const effectiveCompanyId = companyId || localStorageCompanyId;

  // Auto pagination navigation hook
  const { backToListing, toListing } = useCompletePaginationNavigation(navigate, '/users');

  // State
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showChangeReasonPrompt, setShowChangeReasonPrompt] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [rolePage, setRolePage] = useState(1);
  const [allRoles, setAllRoles] = useState<any[]>([]);
  const [hasMoreRoles, setHasMoreRoles] = useState(true);

  const { control, setValue, watch } = useForm({
    defaultValues: { phone_number: '' }
  });

  const watchedPhone = watch('phone_number');

  const [formData, setFormData] = useState<Partial<User>>({
    first_name: '',
    last_name: '',
    email: '',
    position: '',
    employee_id: '',
    phone_number: '',
    reporting_manager: '',
    is_active: true,
    department: '',
    role_ids: '',
    additional_info: {
      profile_picture: '',
    },
  });

  const { data: phoneCountryCodes = [], isLoading: isPhoneCodesLoading, error: phoneCodesError } = useGetPhoneCountryCodesQuery();

  useEffect(() => {
    if (watchedPhone !== undefined && watchedPhone !== formData.phone_number) {
      setFormData(prev => ({ ...prev, phone_number: watchedPhone }));
    }
  }, [watchedPhone]);

  // Department list (dynamically load)
  const {
    data: departmentListData,
    isLoading: isDepartmentsLoading,
    error: departmentsError,
  } = useGetDepartmentsDropdownQuery({ companyId: effectiveCompanyId });

  // workflow transaction mutation
  const [updateTransaction] = useUpdateWorkflowTransactionMutation();

  const {
    data: rolesData,
    isLoading: isRolesLoading,
    error: rolesError,
    refetch: refetchRoles,
    isFetching: isRolesFetching
  } = useGetRolesDropdownQuery(
    { page: rolePage, limit: 20, search: roleSearch || undefined },
  );

  useEffect(() => {
    if (rolesData?.results) {
      if (rolePage === 1) {
        setAllRoles(rolesData.results);
      } else {
        setAllRoles(prev => {
          const ids = new Set(prev.map(r => r.id));
          const newItems = rolesData.results.filter((r:any) => !ids.has(r.id));
          return [...prev, ...newItems];
        });

      }
      setHasMoreRoles(Boolean(rolesData.next));
    }
  }, [rolesData, rolePage]);

  const roleOptions = useMemo(
    () =>
      allRoles.map((role) => ({
        label: `${role.name}${role.description ? ` (${role.description})` : ''}`,
        value: role.id,
      })),
    [allRoles]
  );

  const handleRoleScrollEnd = () => {
    if (hasMoreRoles && !isRolesLoading && !isRolesFetching) {
      setRolePage(prev => prev + 1);
    }
  };

  // User list for managers
  const {
    data: allUsersResult,
    isLoading: isUsersLoading,
    error: usersError,
  } = useListAllUsersQuery();
  const users: User[] = Array.isArray(allUsersResult?.results)
    ? allUsersResult.results
    : [];

  // User data for editing
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: fetchError,
  } = useGetUserByIdQuery(userId || '', { skip: !userId });

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  // Header shows the saved record, not unsaved form edits
  const savedUser = isWorkflowEdit ? initialData : userData;

  // Reset Password — admin-triggered, only for users who already have a
  // password-based account (direct-creation users, or invite-mode users who
  // already accepted their invite). A pending invite has no password yet and
  // is handled by Resend Invitation instead. The admin never sees/types the
  // new password directly — it's generated server-side and handed over once
  // as a downloaded spreadsheet.
  const [resetPassword, { isLoading: isResettingPassword }] = useResetPasswordMutation();
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const canResetPassword =
    Boolean(userData?.auth0_user_id) &&
    !(userData?.invitation_sent_at && !userData?.invitation_accepted_at);

  const handleConfirmResetPassword = async () => {
    if (!userId) return;
    try {
      const result = await resetPassword(userId).unwrap();
      downloadCredentialsExcel(result);
      toast.success('Password reset successfully. New credentials downloaded.');
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to reset password');
    } finally {
      setShowResetConfirm(false);
    }
  };

  // Set form data
  useEffect(() => {
    if (!isWorkflowEdit && userData) {
      let phoneValue = '';
      if (userData.phone_number_detail) {
        const { dial_code, national_number } = userData.phone_number_detail;
        phoneValue = `${dial_code} ${national_number}`.trim();
      } else if (userData.phone_number) {
        phoneValue = userData.phone_number;
      }
      
      setValue('phone_number', phoneValue);
      
      setFormData({
        ...userData,
        phone_number: phoneValue,
        role_ids: userData?.roles?.[0]?.id || '',
        additional_info: userData.additional_info || {
          profile_picture: '',
        },
      });
    }
    if(isWorkflowEdit && initialData) {
      let phoneValue = '';
      if (initialData.phone_number_detail) {
        const { dial_code, national_number } = initialData.phone_number_detail;
        phoneValue = `${dial_code} ${national_number}`.trim();
      } else if (initialData.phone_number) {
        phoneValue = initialData.phone_number;
      }
      
      setValue('phone_number', phoneValue);
      
      setFormData({
        ...initialData,
        phone_number: phoneValue,
        additional_info: initialData.profile || {
          profile_picture: '',
        },
      });
    }
  }, [userData, initialData, isWorkflowEdit, setValue]);

  useEffect(() => {
    if (fetchError) {
      setError('Failed to load user details');
      // eslint-disable-next-line no-console
      console.error('Error loading user:', fetchError);
    }
  }, [fetchError]);

  // Input change handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (!name) return;
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (name.startsWith('additional_info.')) {
      const fieldName = name.split('.')[1];
      setFormData({
        ...formData,
        additional_info: {
          ...formData.additional_info,
          [fieldName]:
            type === 'checkbox'
              ? (e.target as HTMLInputElement).checked
              : value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]:
          type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
      });
    }
  };

  // For Select
  const handleSelectChange = (
    e: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (!name) return;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    if (!formData.first_name) {
      setError('First name is required');
      return false;
    }
    if (!formData.last_name) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (userData) {
      const hasChanges = Object.keys(formData).some((key) => {
        if (key === 'additional_info') {
          return (
            JSON.stringify(formData[key]) !== JSON.stringify(userData[key])
          );
        }
        // @ts-ignore
        return formData[key as keyof User] !== userData[key as keyof User];
      });
      if (hasChanges && !changeReason && !showChangeReasonPrompt) {
        setShowChangeReasonPrompt(true);
        return;
      }
    }
    try {
      setError(null);
      setFieldErrors({});
      const roleIds: string[]  = [];
      if (formData.role_ids) {
        if (Array.isArray(formData.role_ids)) {
          roleIds.push(...formData.role_ids);
        } else {
          roleIds.push(formData.role_ids);
        }
      }
      if (userId) {
        const updatePayload = {
          ...formData,
          role_ids: roleIds,
          change_reason: changeReason,
        };
        delete updatePayload.id;
        delete updatePayload.email;
        delete updatePayload.auth0_user_id;
        delete updatePayload.invitation_sent_at;
        delete updatePayload.invitation_accepted_at;
        await updateUser({
          userId,
          userData: updatePayload,
        }).unwrap();
        setSuccess('User updated successfully');
        setTimeout(() => toListing(), 2000);
      }
      if (isWorkflowEdit && workflowTransaction) {
        const updatePayload = {
          ...formData,
        };
        delete updatePayload.id;
        delete updatePayload.email;
        delete updatePayload.auth0_user_id;
        delete updatePayload.invitation_sent_at;
        delete updatePayload.invitation_accepted_at;
        await updateTransaction({
          id: workflowTransaction,
          formData: updatePayload,
        }).unwrap();
        if(setIsEditWorkflow) {
          setIsEditWorkflow(false);
        }
        if(approvalTaskRefetch){
          approvalTaskRefetch();
        }
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error updating user:', err);
      const { fieldErrors: apiFieldErrors, message } = parseApiError(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        // Surface server-side (API) validation errors under their field, same
        // as client-side ones; editing the field clears it via handleInputChange.
        setFieldErrors(apiFieldErrors);
      } else {
        setError(message || (err instanceof Error ? err.message : 'Failed to update user'));
      }
    }
  };

  const handleCancel = () => backToListing();

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return formatDateUtil(dateString, 'MMM d, yyyy h:mm a', dateString);
  };

  if (isLoadingUser || isUsersLoading || isDepartmentsLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {!isWorkflowEdit &&
      <Box mb={2}>
        <Button
          variant="outlined"
          onClick={backToListing}
          startIcon={<img src={Images.BACK} alt="Back" width="22" />}
        >
          Back to Users
        </Button>
      </Box>
      }
      {(error || departmentsError) && Object.keys(fieldErrors).length === 0 && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: 'error.light',
            color: 'error.contrastText',
          }}
        >
          <Typography>
            {error ||
             (departmentsError ? 'Failed to load departments.' : '')}
          </Typography>
        </Paper>
      )}
      {success && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: 'success.light',
            color: 'success.contrastText',
          }}
        >
          <Typography>{success}</Typography>
        </Paper>
      )}
      <Card>
        <CardContent sx={{ px: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center">
              <Avatar
                sx={{ width: 80, height: 80, mr: 3 }}
                alt={`${savedUser?.first_name || ''} ${savedUser?.last_name || ''}`}
                src={formData.additional_info?.profile_picture}
              />
              <Box>
                <Typography variant="h5">
                  {savedUser?.first_name} {savedUser?.last_name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {formData.email}
                </Typography>
              </Box>
            </Box>
            {!isWorkflowEdit && canResetPassword && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowResetConfirm(true)}
                disabled={isResettingPassword}
              >
                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            )}
          </Box>

          <Dialog open={showResetConfirm} onClose={() => setShowResetConfirm(false)}>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will generate a new password for {formData.email} and invalidate their
                current one immediately. The new credentials will be downloaded as a spreadsheet —
                share it with the user out-of-band. This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowResetConfirm(false)} disabled={isResettingPassword}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmResetPassword}
                color="primary"
                variant="contained"
                disabled={isResettingPassword}
              >
                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Form Starts */}
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Typography variant="h6" sx={{ mb: 1 }}>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={6} mb={4} alignItems="stretch">
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  First Name *
                </Typography>
                <TextField
                  fullWidth
                  required
                  placeholder="First Name"
                  name="first_name"
                  value={formData.first_name || ''}
                  onChange={handleInputChange}
                  error={Boolean(fieldErrors.first_name)}
                  helperText={fieldErrors.first_name || ''}
                  disabled={isWorkflowEdit && !isFieldEnabled("first_name", modifiable)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Last Name *
                </Typography>
                <TextField
                  fullWidth
                  required
                  placeholder="Last Name"
                  name="last_name"
                  value={formData.last_name || ''}
                  onChange={handleInputChange}
                  error={Boolean(fieldErrors.last_name)}
                  helperText={fieldErrors.last_name || ''}
                  disabled={isWorkflowEdit && !isFieldEnabled("last_name", modifiable)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Position
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Position"
                  name="position"
                  value={formData.position || ''}
                  onChange={handleInputChange}
                  error={Boolean(fieldErrors.position)}
                  helperText={fieldErrors.position || ''}
                  disabled={isWorkflowEdit && !isFieldEnabled("position", modifiable)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Employee ID *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Employee ID"
                  name="employee_id"
                  value={formData.employee_id || ''}
                  onChange={handleInputChange}
                  error={Boolean(fieldErrors.employee_id)}
                  helperText={fieldErrors.employee_id || ''}
                  disabled={isWorkflowEdit && !isFieldEnabled("employee_id", modifiable)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Contact Number *
                </Typography>
                <Controller
                  name="phone_number"
                  control={control}
                  defaultValue={formData.phone_number || ''}
                  render={() => (
                    <PhoneInput
                      control={control}
                      name="phone_number"
                      countryCodes={phoneCountryCodes.map((country) => ({
                        code: country.dial_code,
                        flag: country.region,
                      }))}
                      placeholder="Phone Number"
                      disabled={isWorkflowEdit && !isFieldEnabled("phone_number", modifiable)}
                      isLoading={isPhoneCodesLoading}
                      hasError={!!phoneCodesError}
                    />
                  )}
                />
              </Grid>
              {/* ---- ROLE SELECT ---- */}
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Role *
                </Typography>
                <FormControl fullWidth sx={{ minWidth: 220 }}>
                  <AutoCompleteSelect
                    fullwidth
                    options={roleOptions}
                    value={formData.role_ids || ''}
                    loading={isRolesLoading || isRolesFetching}
                    disabled={isWorkflowEdit && !isFieldEnabled("role_ids", modifiable)}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        role_ids: value as string,
                      }))
                    }
                    placeholder="Select Role*"
                    inputValue={roleSearch}
                    onInputChange={(value) => {
                      setRoleSearch(value);
                      setRolePage(1);
                    }}
                    onScrollEnd={handleRoleScrollEnd}
                  />
                  {fieldErrors.role_ids && (
                    <FormHelperText error>{fieldErrors.role_ids}</FormHelperText>
                  )}
                  {rolesError && (
                    <FormHelperText error>Error loading roles</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {/* ------ DYNAMIC DEPARTMENT SELECT ------ */}
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <FormControl fullWidth>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                    Department
                  </Typography>
                  <Select
                    name="department"
                    value={formData.department || ''}
                    onChange={handleSelectChange}
                    placeholder="Department"
                    disabled={isWorkflowEdit && !isFieldEnabled("department", modifiable)}
                  >
                    {(departmentListData?.results || []).map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                    {(!departmentListData?.results ||
                      departmentListData.results.length === 0) && (
                      <MenuItem disabled>No departments found</MenuItem>
                    )}
                  </Select>
                  {fieldErrors.department && (
                    <FormHelperText error>{fieldErrors.department}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {/* REPORTING MANAGER DROPDOWN */}
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <FormControl fullWidth>
                  <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                    Reporting Manager
                  </Typography>
                  <Select
                    labelId="reporting-manager-label"
                    name="reporting_manager"
                    value={formData.reporting_manager || ''}
                    onChange={handleSelectChange}
                    placeholder="Reporting Manager"
                    disabled={isUsersLoading || (isWorkflowEdit && !isFieldEnabled("reporting_manager", modifiable))}
                  >
                    {isUsersLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={24} />
                      </MenuItem>
                    ) : usersError ? (
                      <MenuItem disabled>Error loading users</MenuItem>
                    ) : (
                      users
                        .filter((u) => u.id !== userId)
                        .map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </MenuItem>
                        ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active === true}
                      onChange={handleInputChange}
                      name="is_active"
                      color="primary"
                      disabled={isWorkflowEdit && !isFieldEnabled("is_active", modifiable)}
                    />
                  }
                  label="Active User"
                  sx={{ ml: 1 }}
                />
              </Grid>
            </Grid>

            {/* System Information */}
            <Typography variant="h6" sx={{ mb: 1 }}>
              System Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Email"
                  value={formData.email || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Invitation Sent
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Invitation Sent"
                  value={formatDate(formData.invitation_sent_at)}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={FIELD_GRID}>
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                  Invitation Accepted
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Invitation Accepted"
                  value={formatDate(formData.invitation_accepted_at)}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
            </Grid>

            {/* Change reason prompt */}
            {showChangeReasonPrompt && (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Change Reason
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ mb: 1, fontWeight: 500 }}>
                      Reason for Changes
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      placeholder="Reason for Changes"
                      name="changeReason"
                      value={changeReason}
                      onChange={(e) => setChangeReason(e.target.value)}
                      margin="normal"
                      multiline
                      rows={3}
                      error={!changeReason}
                      helperText={
                        !changeReason
                          ? 'Please provide a reason for the changes'
                          : ''
                      }
                      disabled={isWorkflowEdit && !isFieldEnabled("changeReason", modifiable)}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {/* Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              {!isWorkflowEdit &&
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              }
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isUpdating}
                startIcon={isUpdating ? <CircularProgress size={20} /> : null}
              >
                {isUpdating ? 'Updating...' : isWorkflowEdit ? 'Save' : 'Update User'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UserEdit;